import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { Venue } from '@cocoonspace/sdk-js/types/venue'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import {
	useGetVenue,
	useListSpaces,
} from '@cocoonspace/sdk-js/domains/spaces/spaces'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { DialogHeader, DialogTitle } from '@cocoonspace/ui/components/dialog'
import { Label } from '@cocoonspace/ui/components/label'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { keepPreviousData } from '@tanstack/react-query'
import { TwicImg } from '@twicpics/components/react'
import { MapPinIcon } from 'lucide-react'

import { CheckStateIcon } from '~/components/check-state-icon'
import { Price } from '~/components/price'
import { SpaceCapacity } from '~/domains/spaces/components/atoms/space-capacity'
import { SpaceSurface } from '~/domains/spaces/components/atoms/space-surface'
import { useStep } from './provider'
import type { LocationCocoonSchema } from '../../event-type-form'

const MAX_SPACES = 5

const VenueLoader = () => (
	<Card
		variant='outline'
		className='flex flex-1 flex-col gap-2 p-4 py-2'
	>
		<Skeleton className='h-4 w-[120px]' />
		<Skeleton className='h-3 w-[180px]' />
	</Card>
)

const VenueDetails = ({ venue }: { venue: Venue }) => {
	const address = useMemo(() => {
		const { address1, postal_code, city } = venue.address ?? {}

		return `${address1 || ''}, ${postal_code || ''} ${city || ''}`
	}, [venue])

	return (
		<Card
			variant='outline'
			className='w-full px-4 py-2'
		>
			<div className='text-sm font-bold'>{venue.name}</div>

			<div className='text-muted-foreground flex flex-row items-center gap-1 text-xs'>
				<MapPinIcon />
				{address}
			</div>
		</Card>
	)
}

const SpacePrice = ({ space }: { space: Space }) => {
	if (!space.prices?.weekday_min?.hour_standard) {
		return null
	}

	return (
		<span className='text-xs'>
			<Trans>À partir de</Trans>{' '}
			<Price price={space.prices.weekday_min.hour_standard} />
		</span>
	)
}

const SpaceCardLoader = () =>
	Array.from({ length: 6 }).map((_, i) => (
		<Card
			key={i}
			variant='outline'
			className='flex w-full flex-row items-center gap-4 pr-4 text-sm'
		>
			<div className='bg-muted/10 h-[64px] w-24 overflow-hidden rounded-l' />

			<div className='flex flex-1 flex-col gap-2'>
				<Skeleton className='h-4 w-[200px]' />

				<div className='flex flex-row items-center gap-2 text-sm'>
					<Skeleton className='h-3 w-[30px]' />
					<Skeleton className='h-3 w-[60px]' />
				</div>
			</div>

			<Skeleton className='size-4 rounded-sm' />
		</Card>
	))

const SpaceCard = ({ space }: { space: Space }) => {
	const { watch, setValue } = useFormContext<LocationCocoonSchema>()
	const spaceIds = watch('space_ids')

	const isActive = spaceIds.includes(space.id)

	const handleClick = () => {
		if (isActive) {
			setValue(
				'space_ids',
				spaceIds.filter((id) => id !== space.id),
				{ shouldDirty: true, shouldValidate: true },
			)
		} else {
			setValue('space_ids', [...spaceIds, space.id], {
				shouldDirty: true,
				shouldValidate: true,
			})
		}
	}

	return (
		<Card
			variant='outline'
			className={cn('flex w-full flex-row items-center gap-4 pr-4 text-sm', {
				'border-primary': isActive,
				'opacity-50': spaceIds.length >= MAX_SPACES && !isActive,
			})}
			onClick={
				spaceIds.length >= MAX_SPACES && !isActive ? undefined : handleClick
			}
		>
			<div className='bg-muted/10 h-full w-24 overflow-hidden rounded-l'>
				<TwicImg
					src={space.images[0].path}
					alt={space.title}
					ratio='3/2'
					focus='50px100p'
					className='h-full w-full object-cover'
				/>
			</div>

			<div className='flex-1'>
				<div className='text-sm font-bold'>{space.title}</div>
				<div className='flex flex-row items-center gap-2 text-xs'>
					<SpaceCapacity space={space} />
					<SpaceSurface space={space} />

					{!!space.prices?.weekday_min?.hour_standard && (
						<>
							-
							<SpacePrice space={space} />
						</>
					)}
				</div>
			</div>

			<CheckStateIcon isActive={isActive} />
		</Card>
	)
}

const NoVenueMsg = () => {
	const { t } = useLingui()
	const { goToPreviousStep } = useStep()

	return (
		<div>
			<div>{t`Vous devez d'abord sélectionner une adresse`}</div>

			<Button
				size='sm'
				onClick={goToPreviousStep}
			>
				{t`Précédent`}
			</Button>
		</div>
	)
}

export const StepSpaces = () => {
	const { t } = useLingui()

	const form = useFormContext<LocationCocoonSchema>()
	const venueId = form.watch('venue_id')
	const spaceIds = form.watch('space_ids')

	const { data: venue, isLoading: isLoadingVenue } = useGetVenue(
		venueId ?? '',
		undefined,
		{ query: { placeholderData: keepPreviousData } },
	)

	const { data: spacesByVenueId, isLoading: isLoadingSpaces } = useListSpaces(
		{
			offer: ['cocoon'],
			venues: venueId ? [venueId] : undefined,
		},
		{
			query: {
				placeholderData: keepPreviousData,
				select: ({ data }) => {
					return Object.groupBy(
						data,
						(space) => space.venue_slug ?? '',
					) as Record<string, Space[]>
				},
			},
		},
	)

	const noVenue = !venueId || (!isLoadingVenue && !venue)

	if (noVenue) return <NoVenueMsg />

	return (
		<>
			<DialogHeader className='p-6 pb-0'>
				<DialogTitle>{t`Sélectionnez vos Cocoons`}</DialogTitle>
			</DialogHeader>

			<div className='flex w-full flex-1 flex-col gap-4 overflow-hidden'>
				<div className='flex w-full flex-col gap-2 px-6 pt-4'>
					<Label className='text-xs'>{t`Adresse`}</Label>
					{isLoadingVenue && <VenueLoader />}
					{!isLoadingVenue && venue && <VenueDetails venue={venue.data} />}
				</div>

				<div className='flex w-full flex-1 flex-col gap-2 overflow-y-hidden'>
					<Label className='px-6 text-xs'>
						{t`Sélectionnez les Cocoons (${spaceIds.length || 0}/${MAX_SPACES} max)`}
					</Label>

					<div className='flex w-full flex-col gap-2 overflow-y-auto px-6 pb-6'>
						{isLoadingSpaces && <SpaceCardLoader />}

						{spacesByVenueId?.[venue?.data.slug ?? '']?.map((space) => (
							<SpaceCard
								key={space.id}
								space={space}
							/>
						))}
					</div>
				</div>
			</div>
		</>
	)
}
